import { TaxIncentiveStatuses } from "@prisma/client";

/**
 * Per-location spend, stored on `LedgerCalculation.spendByLocation`.
 *
 * Minimum-spend progress used to cost one `entry.aggregate` per incentive
 * program per render. Every one of those aggregates reads the same upload, so
 * the whole set collapses into a single grouping done once at calculation time
 * — which is also the only moment the numbers can change.
 */
export type LocationSpend = {
  location: string;
  totalSpend: number;
  qualifiedSpend: number;
  transactionCount: number;
};

/**
 * One row of `entry.groupBy({ by: ["location", "taxIncentiveStatus"] })`.
 * Kept structural rather than importing Prisma's generated group type so the
 * folding logic stays testable with plain objects.
 */
export type LocationSpendGroup = {
  location: string | null;
  taxIncentiveStatus: TaxIncentiveStatuses | null;
  totalAmount: number;
  transactionCount: number;
};

const emptySpend = (location: string): LocationSpend => ({
  location,
  totalSpend: 0,
  qualifiedSpend: 0,
  transactionCount: 0,
});

/**
 * Folds grouped entry rows into one record per location.
 *
 * Entries with no location are dropped rather than bucketed under a placeholder
 * — a program's minimum spend is only ever credited from locations an editor
 * has explicitly mapped to it, so an unplaceable entry cannot count toward one.
 */
export const buildLocationSpend = (
  groups: LocationSpendGroup[]
): LocationSpend[] => {
  const byLocation = groups.reduce<Record<string, LocationSpend>>(
    (spend, group) => {
      const location = group.location;
      if (!location) return spend;

      const current = spend[location] || emptySpend(location);
      const isQualified =
        group.taxIncentiveStatus === TaxIncentiveStatuses.QUALIFIED;

      spend[location] = {
        location,
        totalSpend: current.totalSpend + group.totalAmount,
        qualifiedSpend:
          current.qualifiedSpend + (isQualified ? group.totalAmount : 0),
        transactionCount: current.transactionCount + group.transactionCount,
      };

      return spend;
    },
    {}
  );

  return Object.values(byLocation).sort((a, b) =>
    a.location.localeCompare(b.location)
  );
};

/**
 * `distinctLocations` has always been the sorted list of non-empty locations on
 * an upload. Deriving it from the same fold keeps the two fields from
 * disagreeing about what locations exist.
 */
export const extractDistinctLocations = (spend: LocationSpend[]): string[] =>
  spend.map((entry) => entry.location).sort((a, b) => a.localeCompare(b));

export type LocationSpendTotals = {
  totalSpend: number;
  qualifiedSpend: number;
  transactionCount: number;
};

/**
 * Sums the mapped locations for one program. Locations the upload has never
 * seen contribute nothing rather than erroring — mappings are edited by hand and
 * routinely name a location before any entry arrives for it.
 */
export const sumSpendForLocations = (
  spend: LocationSpend[],
  locations: string[]
): LocationSpendTotals => {
  const wanted = new Set(locations);

  return spend.reduce<LocationSpendTotals>(
    (totals, entry) =>
      wanted.has(entry.location)
        ? {
            totalSpend: totals.totalSpend + entry.totalSpend,
            qualifiedSpend: totals.qualifiedSpend + entry.qualifiedSpend,
            transactionCount:
              totals.transactionCount + entry.transactionCount,
          }
        : totals,
    { totalSpend: 0, qualifiedSpend: 0, transactionCount: 0 }
  );
};

/**
 * Progress toward a program's minimum spend, capped at 100.
 *
 * A program with no minimum reports 0 rather than 100: "complete" would imply a
 * threshold was cleared, and there isn't one to clear.
 */
export const percentOfMinimumSpend = (
  actualSpend: number,
  minimumSpend: number
): number => {
  if (!minimumSpend || minimumSpend <= 0) return 0;

  return Math.min(100, (actualSpend / minimumSpend) * 100);
};
